import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { registerCharts, barOptions, barDataset, CHART } from '../components/ChartTheme';
import AppLayout from '../components/AppLayout';
import api from '../axios';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CategoryIcon from '@mui/icons-material/Category';

registerCharts();

const fmtR$ = (v) => (v == null ? '—' : `R$ ${(v / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);

export default function FinanceiroDashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/financeiro/dashboard')
      .then(res => setDash(res.data))
      .catch(err => setErro(errorMsg(err) || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { icon: <AccountBalanceIcon />, value: fmtR$(dash?.saldo_atual), label: 'Saldo atual' },
    { icon: <ArrowUpwardIcon />, value: fmtR$(dash?.receitas_mes), label: 'Receitas do mês' },
    { icon: <ArrowDownwardIcon />, value: fmtR$(dash?.despesas_mes), label: 'Despesas do mês' },
    { icon: <TrendingUpIcon />, value: fmtR$(dash?.resultado), label: 'Resultado' },
  ];

  const receitas = Array.isArray(dash?.receitas_por_categoria) ? dash.receitas_por_categoria : [];
  const despesas = Array.isArray(dash?.despesas_por_categoria) ? dash.despesas_por_categoria : [];
  const catLabels = [...new Set([...receitas.map(r => r.categoria), ...despesas.map(d => d.categoria)])];

  const catData = catLabels.map(cat => {
    const rec = receitas.find(r => r.categoria === cat)?.total || 0;
    const desp = despesas.find(d => d.categoria === cat)?.total || 0;
    return { cat, receitas: rec, despesas: desp };
  });

  const atalhos = [
    { label: 'Financeiro', icon: <AccountBalanceIcon />, path: '/financeiro' },
    { label: 'Lançamentos', icon: <ListAltIcon />, path: '/financeiro/lancamentos' },
    { label: 'Categorias', icon: <CategoryIcon />, path: '/financeiro/categorias' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: CHART.teal, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Módulo</Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, fontWeight: 800 }}>Financeiro</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>Fluxo de caixa, receitas, despesas e resultados.</Typography>
        </Box>

        {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
        {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

        {dash && (
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpis.map((k, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHART.teal, flexShrink: 0 }}>{k.icon}</Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2rem', color: 'text.primary', lineHeight: 1.1 }}>{k.value}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3, mb: 4 }}>
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>Por categoria</Typography>
              <Box sx={{ height: 240 }}>
                <Bar data={{
                  labels: catLabels,
                  datasets: [
                    { ...barDataset(catData.map(d => d.receitas)), label: 'Receitas', backgroundColor: CHART.teal },
                    { ...barDataset(catData.map(d => d.despesas)), label: 'Despesas', backgroundColor: CHART.lime },
                  ],
                }} options={barOptions({ plugins: { legend: { display: true, position: 'bottom', labels: { color: CHART.muted, usePointStyle: true } } } })} />
              </Box>
            </Card>

            <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Atalhos</Typography>
            <Grid container spacing={1.5}>
              {atalhos.map(a => (
                <Grid item xs={12} sm={6} md={3} key={a.path}>
                  <Button variant="outlined" onClick={() => navigate(a.path)} endIcon={<ArrowForwardIcon />} fullWidth
                    sx={{ justifyContent: 'space-between', borderColor: 'rgba(0,241,167,0.4)', color: CHART.teal, fontWeight: 700, borderRadius: 1, px: 2, py: 1.2, '&:hover': { borderColor: CHART.teal, bgcolor: 'rgba(0,241,167,0.08)' } }}>
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </AppLayout>
  );
}
